/**
 * UploadSourceDialog — modal para subir un Source.
 *
 * Reutilizable para project sources (con phaseId) y tenant sources
 * (sin phaseId). El parent decide qué API client invocar via la prop
 * `onUpload`.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Upload } from 'lucide-react'

import {
  SOURCE_ORIGINS,
  type SourceOrigin,
} from '../../api/sources'
import { Dialog } from '../Dialog'

export interface PhaseOption {
  /** ID del project_phase. Empty string = "sin fase". */
  id: string
  label: string
}

export interface UploadSourceDialogProps {
  open: boolean
  onClose: () => void
  /** Si se pasa, el dialog muestra un combo de fase. Si no, solo se
   * suben sources globales (caso tenant_sources). */
  phaseOptions?: PhaseOption[] | undefined
  /** Phase preseleccionada (cuando se abre desde el drill-in de una
   * fase específica). */
  defaultPhaseId?: string | undefined
  /** El parent maneja la mutation (TanStack Query). Recibe los datos
   * del form y devuelve una Promise. El dialog sólo se cierra si la
   * promesa resuelve OK. */
  onUpload: (input: {
    file: File
    origin: SourceOrigin
    phaseId: string | undefined
    tags: string[]
    description: string | undefined
  }) => Promise<void>
}

export function UploadSourceDialog({
  open,
  onClose,
  phaseOptions,
  defaultPhaseId,
  onUpload,
}: UploadSourceDialogProps) {
  const { t } = useTranslation('sources')

  const [file, setFile] = useState<File | null>(null)
  const [origin, setOrigin] = useState<SourceOrigin>('other')
  const [phaseId, setPhaseId] = useState<string>(defaultPhaseId ?? '')
  const [tags, setTags] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function reset() {
    setFile(null)
    setOrigin('other')
    setPhaseId(defaultPhaseId ?? '')
    setTags('')
    setDescription('')
    setError(null)
    setSubmitting(false)
  }

  function handleClose() {
    if (submitting) return
    reset()
    onClose()
  }

  async function handleSubmit() {
    if (!file) {
      setError(t('upload.errors.file_required'))
      return
    }
    setSubmitting(true)
    setError(null)
    const cleanTags = tags
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
    try {
      await onUpload({
        file,
        origin,
        phaseId: phaseId || undefined,
        tags: cleanTags,
        description: description.trim() || undefined,
      })
      reset()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'unknown')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('upload.title')}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="px-4 py-2 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
          >
            {t('upload.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !file}
            className="px-5 py-2 bg-brand hover:bg-brand-deep text-canvas text-sm font-medium rounded-md flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-4 h-4" />
            {submitting ? t('upload.submitting') : t('upload.submit')}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className="text-xs font-medium text-ink-muted">
            {t('upload.file_label')}
          </span>
          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            disabled={submitting}
            className="mt-1 w-full text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-brand-soft file:text-brand hover:file:bg-brand-soft/70 file:cursor-pointer"
          />
          {file ? (
            <span className="mt-1 block text-[11px] text-ink-subtle">
              {file.name} · {formatSize(file.size)}
            </span>
          ) : null}
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-ink-muted">
              {t('upload.origin_label')}
            </span>
            <select
              value={origin}
              onChange={(e) => setOrigin(e.target.value as SourceOrigin)}
              disabled={submitting}
              className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm"
            >
              {SOURCE_ORIGINS.map((o) => (
                <option key={o} value={o}>
                  {t(`origins.${o}` as 'origins.email')}
                </option>
              ))}
            </select>
          </label>
          {phaseOptions ? (
            <label className="block">
              <span className="text-xs font-medium text-ink-muted">
                {t('upload.phase_label')}
              </span>
              <select
                value={phaseId}
                onChange={(e) => setPhaseId(e.target.value)}
                disabled={submitting}
                className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm"
              >
                <option value="">{t('upload.phase_none')}</option>
                {phaseOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>

        <label className="block">
          <span className="text-xs font-medium text-ink-muted">
            {t('upload.tags_label')}
          </span>
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder={t('upload.tags_placeholder')}
            disabled={submitting}
            className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand"
          />
          <span className="mt-1 block text-[11px] text-ink-subtle">
            {t('upload.tags_help')}
          </span>
        </label>

        <label className="block">
          <span className="text-xs font-medium text-ink-muted">
            {t('upload.description_label')}
          </span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={submitting}
            rows={2}
            className="mt-1 w-full bg-canvas border border-line rounded-md px-3 py-2 text-sm focus:outline-none focus:border-brand"
          />
        </label>

        {error ? (
          <div className="rounded-md border border-critical/40 bg-critical-soft p-2.5 font-mono text-xs text-critical">
            {error}
          </div>
        ) : null}
      </div>
    </Dialog>
  )
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
